import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CurrencyFacets from "./CurrencyFacets";
import { CurrencyChip, FacetHeader, FacetWrapper } from "./layout";
import {
  assignCategoryColors,
  SURFACE,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
  GRIDLINE,
} from "./palette";
import { formatAmount, formatCompactAmount } from "./format";
import { buildMonthKeys, buildMonthKeysFromRange } from "./monthKeys";

const SectionHeading = styled.div`
  margin: 0.5em 0 0.25em;
  font-size: 13px;
  font-weight: 600;
  color: ${TEXT_SECONDARY};
`;

const EmptyNote = styled.div`
  color: ${TEXT_MUTED};
`;

// 高亮狀態下每根長條的透明度：未選任何分類時全彩；有選時僅該分類全彩、其餘變淡。
// 抽成純函式方便測試（jsdom 下 ResponsiveContainer 寬度為 0，無法直接斷言渲染出的 SVG）。
export function resolveFillOpacity(highlightedId, categoryId) {
  if (highlightedId === null || highlightedId === undefined) {
    return 1;
  }
  return String(highlightedId) === String(categoryId) ? 1 : 0.2;
}

// 點同一個分類取消高亮，點別的則切換到新分類。
export function toggleHighlight(prevId, nextId) {
  return String(prevId) === String(nextId) ? null : nextId;
}

const TooltipBox = styled.div`
  background: ${SURFACE};
  border: 1px solid rgba(11, 11, 11, 0.1);
  border-radius: 6px;
  padding: 0.5em 0.75em;
  font-size: 0.85em;
`;

const TooltipTitle = styled.div`
  color: ${TEXT_MUTED};
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TooltipSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(props) => props.color};
`;

// 堆疊長條的 tooltip：列出當月每個非零分類的金額（由大到小），最後附上合計。
function StackTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const rows = payload.filter((entry) => entry.value > 0).sort((a, b) => b.value - a.value);
  if (rows.length === 0) {
    return null;
  }

  const total = rows.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <TooltipBox>
      <TooltipTitle>{dayjs(`${label}-01`).format("YYYY年M月")}</TooltipTitle>
      {rows.map((entry) => (
        <TooltipRow key={entry.dataKey}>
          <TooltipSwatch color={entry.color} />
          {`${entry.name} ${formatAmount(entry.value)}`}
        </TooltipRow>
      ))}
      <TooltipTitle as="div" style={{ marginTop: 4, marginBottom: 0 }}>
        {`合計 ${formatAmount(total)}`}
      </TooltipTitle>
    </TooltipBox>
  );
}

StackTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
};

StackTooltip.defaultProps = {
  active: false,
  payload: [],
  label: "",
};

function CurrencyStackedChart({ currencyName, months, rows, colorById }) {
  // 點 legend 高亮／隔離單一分類：其餘色段變淡，補足堆疊圖「跨月比較單一分類」的弱點。
  // 高亮狀態是各 facet（幣別 × 收支型別）獨立的 local state，彼此不互相影響。
  const [highlightedId, setHighlightedId] = useState(null);

  // 這個 facet（幣別 × 收支型別）內實際出現的分類，依 id 排序讓堆疊順序穩定
  const categories = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => byId.set(row.category.id, row.category));
    return [...byId.values()].sort((a, b) => Number(a.id) - Number(b.id));
  }, [rows]);

  // Recharts legend entry 帶 dataKey（即 category id）。點同一個取消高亮、點別的則切換。
  function handleLegendClick(entry) {
    const id = entry && entry.dataKey;
    if (id === undefined || id === null) {
      return;
    }
    setHighlightedId((prev) => toggleHighlight(prev, id));
  }

  // legend 文字：非高亮者變淡、高亮者加粗，並提示可點擊
  function renderLegendText(value, entry) {
    const dimmed = highlightedId !== null && String(entry.dataKey) !== String(highlightedId);
    return (
      <span
        style={{
          color: dimmed ? TEXT_MUTED : TEXT_PRIMARY,
          fontWeight: String(entry.dataKey) === String(highlightedId) ? 600 : 400,
          cursor: "pointer",
        }}
      >
        {value}
      </span>
    );
  }

  const data = useMemo(() => {
    const byMonth = {};
    rows.forEach((row) => {
      if (!byMonth[row.month]) {
        byMonth[row.month] = {};
      }
      byMonth[row.month][row.category.id] = row.total;
    });

    return months.map((month) => ({ month, ...(byMonth[month] || {}) }));
  }, [months, rows]);

  return (
    <FacetWrapper>
      <FacetHeader>
        <CurrencyChip>{currencyName}</CurrencyChip>
      </FacetHeader>
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRIDLINE} />
          <XAxis
            dataKey="month"
            tickFormatter={(month) => dayjs(`${month}-01`).format("M月")}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompactAmount}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip content={<StackTooltip />} cursor={{ fill: "rgba(11,11,11,0.04)" }} />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            onClick={handleLegendClick}
            formatter={renderLegendText}
          />
          {categories.map((category) => (
            <Bar
              key={category.id}
              dataKey={category.id}
              name={category.name}
              stackId="month"
              fill={colorById[category.id]}
              fillOpacity={resolveFillOpacity(highlightedId, category.id)}
              maxBarSize={28}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </FacetWrapper>
  );
}

CurrencyStackedChart.propTypes = {
  currencyName: PropTypes.string.isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  colorById: PropTypes.objectOf(PropTypes.string).isRequired,
};

function TypeSection({ heading, rows, months, colorById }) {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeading>{heading}</SectionHeading>
      <CurrencyFacets rows={rows}>
        {(currency, currencyRows) => (
          <CurrencyStackedChart
            key={currency.id}
            currencyName={currency.name}
            months={months}
            rows={currencyRows}
            colorById={colorById}
          />
        )}
      </CurrencyFacets>
    </>
  );
}

TypeSection.propTypes = {
  heading: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  colorById: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default function MonthlyCategoryChart({
  monthlyCategorySpending,
  months,
  startDate,
  endDate,
}) {
  const monthKeys = useMemo(
    () =>
      startDate && endDate ? buildMonthKeysFromRange(startDate, endDate) : buildMonthKeys(months),
    [months, startDate, endDate]
  );

  // 顏色跟著類別走：以「全部」類別（跨幣別、跨收支）一次指定，
  // 同一類別在不同幣別的圖中才會是同一個顏色（與收支細項分布一致）。
  const colorById = useMemo(
    () => assignCategoryColors((monthlyCategorySpending || []).map((row) => row.category.id)),
    [monthlyCategorySpending]
  );

  const rows = monthlyCategorySpending || [];
  const expenseRows = rows.filter((row) => row.category.type !== "income");
  const incomeRows = rows.filter((row) => row.category.type === "income");

  if (rows.length === 0) {
    return <EmptyNote>目前沒有收支資料可供分析。</EmptyNote>;
  }

  return (
    <>
      <TypeSection heading="支出" rows={expenseRows} months={monthKeys} colorById={colorById} />
      <TypeSection heading="收入" rows={incomeRows} months={monthKeys} colorById={colorById} />
    </>
  );
}

MonthlyCategoryChart.propTypes = {
  monthlyCategorySpending: PropTypes.arrayOf(PropTypes.object),
  months: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

MonthlyCategoryChart.defaultProps = {
  monthlyCategorySpending: null,
  months: undefined,
  startDate: null,
  endDate: null,
};
